import { NewspaperIcon } from '@buffer-mono/popcorn'
import { useState, useMemo } from 'react'
import type { CSSProperties } from 'react'
import { clsx } from 'clsx'
import styles from './FeedIcon.module.css'

const sizeMap = {
  small: 16,
  medium: 32,
  large: 48,
}

type FeedItemIconProps = {
  feedName: string
  url?: string
  size?: 'small' | 'medium' | 'large'
  className?: string
  thumbnailUrl?: string
}

export const FeedItemIcon = (props: FeedItemIconProps): JSX.Element => {
  const {
    feedName,
    url,
    size = 'small',
    className,
    thumbnailUrl,
    ...rest
  } = props
  const [hasError, setHasError] = useState(false)

  const domain = useMemo(() => {
    if (!url) return null
    try {
      return new URL(url).hostname
    } catch {
      return null
    }
  }, [url])

  const iconSize = sizeMap[size] || 16
  /**
   * Some icon sizes make the avatar style look a little weird
   * As these icons are dynamic it's hard to get the size right
   * So we're overriding the size only for the Buffer icon to make it look better
   */
  const bufferIconOverride =
    thumbnailUrl ===
    'https://images.buffer.com/proxy/100x100/https://buffer.com/static/icons/favicon.svg'
      ? 30
      : undefined

  return (
    <div
      className={styles.feedIcon}
      style={
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        {
          '--icon-size': `${iconSize}px`,
          '--icon-border-size': `${size === 'small' ? 1 : 2}px`,
        } as CSSProperties
      }
    >
      {(thumbnailUrl || domain) && !hasError ? (
        <img
          aria-hidden="true"
          src={thumbnailUrl || `https://icons.duckduckgo.com/ip3/${domain}.ico`}
          alt={`${feedName} icon`}
          onError={(e): void => {
            e.currentTarget.onerror = null // Prevent infinite loop
            setHasError(true)
          }}
          className={clsx(styles.image, className)}
          style={
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            {
              '--icon-size': `${bufferIconOverride ?? iconSize}px`,
            } as CSSProperties
          }
          {...rest}
        />
      ) : (
        <NewspaperIcon
          className={className}
          style={
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            {
              '--icon-size': `${iconSize / 1.5}px`,
            } as React.CSSProperties
          }
          {...rest}
        />
      )}
    </div>
  )
}
